import Utils from "../../../utils/utils";
import ROLE, { getRoleLabel } from "../../../modules/role";
import { ROUTES } from "../../../routes";
import ApiUser from "../../../modules/api/user";
import DataTable from "react-data-table-component";
import ApiMember from "../../../modules/api/member";
import FGField from "../../../components/dumb/FGField";
import FGInput from "../../../components/dumb/FGInput";
import FGToggle from "../../../components/dumb/FGToggle";
import usePageTitle from "../../../hooks/use-page-title";
import { useNavigate, useParams } from "react-router-dom";
import Notifier from "../../../modules/notifier/notifier";
import ApiProgramme from "../../../modules/api/programme";
import Debouncer from "../../../modules/debouncer/debouncer";
import FGTextarea from "../../../components/dumb/FGTextarea";
import ApiGenericData from "../../../modules/api/generic-data";
import AppUserContext from "../../../context/app-user-context";
import { useEffect, useRef, useState, useContext } from "react";
import FGDropdownApi from "../../../components/form/FGDropdownApi";
import Multilingual from "../../../modules/multilingual/multilingual";
import {
  PortalContext,
  usePortalSettingSet,
} from "../../../context/portal-context";
import FooterBoxAction from "../../../components/common/FooterBoxAction";
import {
  CONST_SELECT_ROLES,
  CONST_SELECT_USER_STATUS,
  DEFAULT_SELECT_OPTION,
  USER_STATUS_ACTIVE,
} from "../../../config/constants";
import { HiExclamationCircle, HiX } from "react-icons/hi";
import { BsBoxArrowInDown } from "react-icons/bs";
import ModulePopup from "../../../modules/popup/popup";

const UPDATED_FIELDS = [
  "name",
  "nameEmail",
  "email",
  "roles",
  "organization",
  "jobTitle",
  "jobDescription",
  "studentId",
  "programme",
  "sitIds",
  "industryIds",
  "status",
  "isLocked",
] as const;
const FIELDS: { [key in (typeof UPDATED_FIELDS)[number]]: string } =
  UPDATED_FIELDS.reduce((curr, item) => ({ ...curr, [item]: "" }), {}) as any;

export default function PageMemberEdit() {
  const [pageTitle, setPageTitle] = useState("Edit Member");
  usePageTitle("Edit Member");
  usePortalSettingSet("label_header", pageTitle);
  const { setCustomText } = useContext(PortalContext);
  const user = useContext(AppUserContext);
  const navigate = useNavigate();
  const { programmeId, cohortId, groupId, _id, role } = useParams();
  const refDataLoaded = useRef(false);
  const [contentChanged, setContentChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [found, setFound] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({ ...FIELDS });
  const [supervisedStudents, setSupervisedStudents] = useState([]);
  const portalCtx = useContext(PortalContext);

  useEffect(() => {
    if (role !== ROLE.SUPERVISOR && role !== ROLE.INDUSTRY_SUPERVISOR && role !== ROLE.STUDENT) {
      navigate(ROUTES.PAGE_404);
    }
  }, [role]);

  useEffect(() => {
    if (portalCtx && pageTitle) {
      portalCtx.setPortalSetting((d) => ({
        ...d,
        label_header: pageTitle,
      }));
    }
  }, [pageTitle]);

  useEffect(() => {
    setCustomText(pageTitle);
  }, [pageTitle]);

  useEffect(() => {
    setLoading(true);
    ApiUser.get(_id, groupId)
      .then((res) => {
        if (res.status) {
          setFound(true);
          setFormData(checkStatusValue(res.data));
          setPageTitle(`${getRoleLabel(role)} particulars`);
          setTimeout(() => (refDataLoaded.current = true), 250);
        }
      })
      .catch((e) => Notifier.error(e?.message || String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let supervisorRoles = [`${ROLE.SUPERVISOR}`, `${ROLE.INDUSTRY_SUPERVISOR}`];
    if (
      !Array.isArray(formData.roles) ||
      !formData.roles.some((role) => supervisorRoles.includes(role))
    )
      return;
    ApiMember.getSupervisedStudents(_id, groupId)
      .then((res) => {
        if (Array.isArray(res)) setSupervisedStudents(res);
      })
      .catch((e) => Notifier.error(e?.message || String(e)));
  }, [formData.roles]);

  const onChange = (e: any) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((data) => ({ ...data, [e.target.name]: value }));
    if (refDataLoaded.current) {
      setContentChanged(true);
    }
  };

  const onChangeSupervisors = (e: any) => {
    let value = [];
    if (e.target.value) value = e.target.value.split(",");
    setFormData((data) => ({ ...data, [e.target.name]: value }));
  };

  const showAlert = (message: string) => {
    ModulePopup.showAlert({
      title: (
        <>
          <HiExclamationCircle />
          Alert
        </>
      ),
      description: <p>{message}</p>,
      onOK: () => {},
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("formData", formData);

    if (role === ROLE.STUDENT) {
      if (!formData.sitIds || formData.sitIds.length === 0) {
        showAlert(
          "Please ensure that at least one SIT Supervisor is assigned!"
        );
        return;
      }

      if (!formData.industryIds || formData.industryIds.length === 0) {
        showAlert(
          "Please ensure that at least one Industry Supervisor is assigned!"
        );
        return;
      }
    }

    Debouncer.execute("SUBMIT_UPDATE_USER", async () => {
      setSending(true);
      const res = await ApiUser.update(
        _id,
        Object.keys(formData)
          .filter((i) => UPDATED_FIELDS.some((j) => j === i))
          .map((i) => ({
            key: i,
            value: formData[i],
          }))
          .reduce((curr, item) => ({ ...curr, [item.key]: item.value }), {}),
        groupId
      );

      if (res.status) {
        Notifier.success(res.message);
        setSending(false);
        setContentChanged(false);
        navigate(
          ROUTES.PORTAL_GROUPS +
            `/manage-member/${programmeId}/${cohortId}/${groupId}`
        );
      } else {
        setSending(false);
        Notifier.error(res.message);
      }
    });
  };

  const roleOptions = () => {
    let roles = CONST_SELECT_ROLES;
    if (user.role === ROLE.ADMIN) {
      return roles.filter((role) =>
        [ROLE.ADMIN, ROLE.PROGRAMME_ADMIN].some((value) => value === role.value)
      );
    } else {
      return roles.filter((role) =>
        [ROLE.STUDENT, ROLE.SUPERVISOR, ROLE.INDUSTRY_SUPERVISOR].some(
          (value) => value === role.value
        )
      );
    }
  };

  const checkStatusValue = (obj: any) => {
    return {
      ...obj,
      status: obj.status ?? USER_STATUS_ACTIVE,
    };
  };

  return (
    <div data-comp="PageUserRegist">
      {/* <h2 className="fs-title mb-3 hide-mobile-down">{pageTitle}</h2> */}

      {loading && <p className="alert alert-info">Loading data...</p>}

      {!loading && !found && (
        <p className="alert alert-info">User not found.</p>
      )}

      {!loading && found && (
        <form className="form-register" onSubmit={handleSubmit}>
          <div className="row" data-comp="PageMemberEdit">
            <div className={role === ROLE.SUPERVISOR ? "col-md-4" : "col-md-6"}>
              <FGInput
                label="Name"
                name="name"
                value={formData.name}
                onChange={onChange}
                required
                autoFocus
              />
            </div>
            <div className={role === ROLE.SUPERVISOR ? "col-md-4" : "col-md-6"}>
              <FGInput
                label={role === ROLE.SUPERVISOR ? "SIT ID" : "Email"}
                name="email"
                type="email"
                value={formData.email}
                onChange={onChange}
                required
              />
            </div>
            {role === ROLE.SUPERVISOR && (
              <div className="col-md-4">
                <FGInput
                  label="SIT Email Address"
                  name="nameEmail"
                  type="email"
                  value={formData.nameEmail}
                  onChange={onChange}
                  required
                />
              </div>
            )}
            {/* {formData.role === ROLE.STUDENT && (
              <div className="col-md-6">
                <FGInput
                  label="Student ID"
                  name="studentId"
                  value={formData.studentId}
                  onChange={onChange}
                />
              </div>
            )} */}
            {role === ROLE.STUDENT && (
              <div className="col-md-12">
                <FGDropdownApi
                  label="Programme"
                  name="programme"
                  defaultValue={formData.programme}
                  api={() =>
                    ApiProgramme.getOptionsByProgramAdmin(user.referenceUserId)
                  }
                  onChange={onChange}
                />
              </div>
            )}
            {/* Hide role [2025-04-16] client request */}
            {[`${ROLE.SUPERVISOR}`, `${ROLE.INDUSTRY_SUPERVISOR}`].includes(
              role
            ) &&
              false && (
                <div className="col-md-6">
                  <FGDropdownApi
                    label="Role"
                    name="role"
                    options={roleOptions()}
                    defaultValue={null}
                    onChange={onChange}
                  />
                </div>
              )}
            {role === ROLE.STUDENT && (
              <div className="col-md-6">
                <FGInput
                  label="Job Title"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={onChange}
                />
              </div>
            )}
            <div className="col-md-6">
              {role !== ROLE.SUPERVISOR && (
                <FGDropdownApi
                  label="Employer"
                  name="organization"
                  defaultValue={formData.organization}
                  api={ApiGenericData.selectQuery(
                    "Organization",
                    "organization",
                    {
                      ...DEFAULT_SELECT_OPTION,
                    }
                  )}
                  onChange={onChange}
                />
              )}
            </div>
            {role === ROLE.STUDENT && (
              <div className="col-md-12">
                <FGTextarea
                  label="Job Description"
                  name="jobDescription"
                  rows={5}
                  value={formData.jobDescription}
                  onChange={onChange}
                />
              </div>
            )}
            {role === ROLE.STUDENT && (
              <>
                <div className="col-md-6">
                  <FGDropdownApi
                    multiple
                    label="SIT Supervisor(s)"
                    name="sitIds"
                    defaultValue={
                      Array.isArray(formData.sitIds)
                        ? formData.sitIds.join(",")
                        : null
                    }
                    api={() =>
                      ApiMember.getOptionsByGroup(groupId, ROLE.SUPERVISOR)
                    }
                    onChange={onChangeSupervisors}
                    closeMenuOnSelect={false}
                  />
                </div>
                <div className="col-md-6">
                  <FGDropdownApi
                    multiple
                    label="Industry Supervisor(s)"
                    name="industryIds"
                    defaultValue={
                      Array.isArray(formData.industryIds)
                        ? formData.industryIds.join(",")
                        : null
                    }
                    api={() =>
                      ApiMember.getOptionsByGroup(
                        groupId,
                        ROLE.INDUSTRY_SUPERVISOR
                      )
                    }
                    onChange={onChangeSupervisors}
                    closeMenuOnSelect={false}
                  />
                </div>
              </>
            )}
            {supervisedStudents.length > 0 && (
              <div className="col-md-12">
                <FGField label="Students">
                  <div className="basic-rdt">
                    <DataTable
                      columns={[
                        // {
                        //   id: "programmeName",
                        //   name: "Programme",
                        //   cell: (row) => row.programmeName,
                        //   sortable: true,
                        //   sortFunction:
                        //     Utils.sortByObjectPropNatural("programmeName"),
                        // },
                        {
                          id: "name",
                          name: "Name",
                          cell: (row) => row.name,
                          sortable: true,
                          sortFunction: Utils.sortByObjectPropNatural("name"),
                        },
                        {
                          id: "email",
                          name: "Email",
                          cell: (row) => row.email,
                          sortable: true,
                          sortFunction: Utils.sortByObjectPropNatural("email"),
                        },
                        {
                          id: "organizationName",
                          name: "Employer",
                          cell: (row) => row.organizationName,
                          sortable: true,
                          sortFunction:
                            Utils.sortByObjectPropNatural("organizationName"),
                        },
                        {
                          id: "jobTitle",
                          name: "Job Title",
                          cell: (row) => row.jobTitle,
                          sortable: true,
                          sortFunction:
                            Utils.sortByObjectPropNatural("jobTitle"),
                        },
                      ]}
                      data={supervisedStudents}
                    />
                  </div>
                </FGField>
              </div>
            )}
            {role === ROLE.STUDENT && (
              <div className="d-none">
                <hr />
                <div className="col-md-12">
                  <FGDropdownApi
                    variant="normal"
                    label="Status in GROW-Pro"
                    name="status"
                    defaultValue={formData.status}
                    options={CONST_SELECT_USER_STATUS}
                    onChange={onChange}
                  />
                </div>
              </div>
            )}
            <div className="col-md-12 d-none">
              <FGToggle
                label="Lock"
                desc={
                  "When enabled, the member will be unable to sign in the portal."
                }
                name="isLocked"
                checked={!!formData.isLocked}
                className="border-0 p-0"
                onChange={onChange}
                readOnly
              />
              <hr />
            </div>
          </div>
          <div>
            <button
              type="submit"
              className="btn btn-secondary"
              disabled={sending}
            >
              <BsBoxArrowInDown className="icon-pos-upper" /> Save
            </button>{" "}
            <button
              type="button"
              className="btn btn-primary"
              onClick={(e) => {
                e.preventDefault();
                if (window.history.length === 1) {
                  navigate(
                    ROUTES.PORTAL_GROUPS +
                      `/manage-member/${programmeId}/${cohortId}/${groupId}`
                  );
                } else {
                  navigate(-1);
                }
              }}
            >
              <HiX /> Cancel
            </button>
          </div>
        </form>
      )}

      <FooterBoxAction
        backUrl={
          ROUTES.PORTAL_GROUPS +
          `/manage-member/${programmeId}/${cohortId}/${groupId}`
        }
        onBackUrlClick={(e) => {
          if (
            contentChanged &&
            !window.confirm(Multilingual.get("CONFIRM_ACTION_CANT_BE_UNDONE"))
          ) {
            e.preventDefault();
          }
        }}
      />
    </div>
  );
}
